import { FC, useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { toast, ToastContainer } from "react-toastify";
import { toastOptions } from "config/constants";
import { useAppSelector } from "config/hooks";
import actionItemsApi, { IActionItemLink } from "api/actionItems";
import { ActionItemDetails, DoneForm } from "./ActionItemDetails";

// RM-3943: the page behind the emailed no-login link (owl RM-3940), for someone
// assigned by email with no Riivet login, e.g. the Apple Account Holder. Works
// signed in or out, like /invitation. The token stays after the # in the URL
// and goes to owl in the request body.

type TState =
  | { kind: "loading" }
  | { kind: "invalid"; message: string }
  | { kind: "ready"; item: IActionItemLink };

const INVALID =
  "This link has expired or is no longer valid. Ask Riivet for a new one.";

const errorMessage = (err: any): string =>
  err?.response?.data?.error || "Something went wrong";

const ActionItemLink: FC = () => {
  const { token = "" } = useParams();
  const isSignedIn = useAppSelector((state) => state.auth.isSignedIn);
  const [state, setState] = useState<TState>({ kind: "loading" });

  const load = useCallback(async () => {
    try {
      setState({ kind: "ready", item: await actionItemsApi.lookupLink(token) });
    } catch (err: any) {
      setState({
        kind: "invalid",
        message: err?.response?.status === 429 ? errorMessage(err) : INVALID,
      });
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const markDone = async (note?: string) => {
    try {
      const item = await actionItemsApi.markLinkDone(token, note);
      setState({ kind: "ready", item });
    } catch (err: any) {
      // 409 still carries the item's current state.
      const item = err?.response?.data;
      if (err?.response?.status === 409 && item?.title)
        setState({ kind: "ready", item });
      toast.error(errorMessage(err), toastOptions);
    }
  };

  const body = () => {
    if (state.kind === "loading")
      return <p className="text-center text-muted">Loading…</p>;
    if (state.kind === "invalid")
      return <p className="text-center">{state.message}</p>;

    const { item } = state;
    return (
      <>
        <p className="text-center text-muted">
          Riivet needs this from <strong>{item.account.name}</strong>. It was
          sent to {item.email}.
        </p>
        <ActionItemDetails item={item} />
        <Box sx={{ mt: 2 }}>
          {item.can_complete && <DoneForm onDone={markDone} />}
          {item.status === "client_done" && (
            <Typography sx={{ fontWeight: 600 }}>
              Thanks! This is marked done. Riivet will check it.
            </Typography>
          )}
          {item.status === "verified" && (
            <Typography sx={{ fontWeight: 600 }}>
              Riivet has checked this. Nothing more is needed.
            </Typography>
          )}
          {item.status === "cancelled" && (
            <Typography>This request is no longer needed.</Typography>
          )}
        </Box>
      </>
    );
  };

  return (
    <div className="h-100 bg-plum-plate bg-animation">
      <div className="d-flex h-100 justify-content-center align-items-center">
        <Box
          className="mx-auto app-login-box"
          sx={{ width: { xs: "100%", md: "66.66%" } }}
        >
          <div className="modal-dialog w-100 mx-auto">
            <div className="modal-content">
              <div className="modal-body">
                <div className="app-logo mx-auto mb-3" />
                <h4 className="text-center mb-3">Action needed</h4>
                {body()}
              </div>
              <div className="modal-footer clearfix">
                <div className="float-left">
                  {isSignedIn ? (
                    <Link to="/">Back to Riivet</Link>
                  ) : (
                    <Link to="/signin">Riivet sign in</Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Box>
      </div>
      <ToastContainer autoClose={2000} />
    </div>
  );
};

export default ActionItemLink;
